// SetupProgress — the "Getting started" onboarding checklist.
// Spec: docs/spec/core/part2-product-requirements.md §13.13 (Reqs 668-671).
//
// 4-step model (Req 668): Account & sign-in, Connect a security tool
// (required), Connect a chat app (optional), Invite teammates (optional).
// Auto-hides when all *required* steps are done (Req 669).

import { trpc } from '../../api/trpc';
import { useAuth } from '../../hooks/useAuth';

// productTypes that count as a "chat app" connector (Req 670 — collab
// connectors: Slack today, Teams in the future). Matched case-insensitively
// against `Connector.productType`, including the catalog's legacy aliases.
const CHAT_APP_PRODUCT_TYPES = ['slack', 'teams', 'microsoft-teams', 'msteams'];

function isChatAppConnector(productType: string): boolean {
  return CHAT_APP_PRODUCT_TYPES.includes(productType.toLowerCase());
}

export function SetupProgress() {
  const { token } = useAuth();

  const { data: connectors } = trpc.connectors.list.useQuery();
  const connectorList = (connectors ?? []) as Array<{ status: string; productType?: string }>;

  // Invite-teammates signal (Req 670): more than one user OR any invitation.
  const hasSession = !!token;
  const usersQuery = trpc.auth.listUsers.useQuery(
    { sessionToken: token ?? '' },
    { enabled: hasSession },
  );
  const invitationsQuery = trpc.auth.listInvitations.useQuery(
    { sessionToken: token ?? '' },
    { enabled: hasSession },
  );

  // ── Signals (Req 670) ──────────────────────────────────────────────────
  // Account & sign-in: always done by the time the user reaches Settings
  // (registration completed). The 4-step model folds the former
  // "Configure SSO" step into this one — an account that authenticates,
  // whether local or via an IdP, satisfies this step.
  const accountDone = true;

  // Connect a security tool (required): any non-chat connector that is active.
  const hasActiveSecurityConnector = connectorList.some(
    (c) => c.status === 'active' && !isChatAppConnector(c.productType ?? ''),
  );

  // Connect a chat app (optional): any active Slack/Teams connector.
  const hasActiveChatConnector = connectorList.some(
    (c) => c.status === 'active' && isChatAppConnector(c.productType ?? ''),
  );

  // Invite teammates (optional): >1 user in the tenant OR any invitation sent.
  const users = (usersQuery.data ?? []) as unknown[];
  const invitations = (invitationsQuery.data ?? []) as unknown[];
  const teammatesInvited = users.length > 1 || invitations.length > 0;

  const steps = [
    { label: 'Account & sign-in', done: accountDone, optional: false },
    { label: 'Connect a security tool', done: hasActiveSecurityConnector, optional: false },
    { label: 'Connect a chat app', done: hasActiveChatConnector, optional: true },
    { label: 'Invite teammates', done: teammatesInvited, optional: true },
  ];

  const completedCount = steps.filter((s) => s.done).length;
  // Auto-hide condition (Req 669): all *required* steps done — optional
  // steps don't block hiding.
  const requiredDone = steps.filter((s) => !s.optional).every((s) => s.done);

  if (requiredDone) return null;

  return (
    <div className="setup-progress">
      <div className="setup-progress-header">
        <span className="setup-progress-title">Getting started</span>
        <span className="setup-progress-count">{completedCount}/{steps.length}</span>
      </div>
      <div className="setup-progress-bar">
        <div className="setup-progress-fill" style={{ width: `${(completedCount / steps.length) * 100}%` }} />
      </div>
      <div className="setup-progress-steps">
        {steps.map((step, i) => (
          <div key={i} className={`setup-step ${step.done ? 'done' : ''}`}>
            <span className="setup-step-check">{step.done ? '✓' : (i + 1)}</span>
            <span>{step.label}</span>
            {step.optional && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 9,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  padding: '1px 5px',
                  borderRadius: 'var(--radius-sm, 4px)',
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-text-muted)',
                }}
              >
                optional
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
